import { Box, Stack } from "@mui/material";
import CampaignManagerSkeletons from "./CampaignManagerSkeletons";
import { useBoundStore } from "useStore";
import { useShallow } from "zustand/react/shallow";
import useCampaignQuery from "./Queries/CampaignQuery";
import { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdsPage from "./AdsPage";
import CampaignPage from "./CampaignPage";

export default function CampaignManagerTab({ page }) {
  const navigate = useNavigate();
  const { selectedCampaign, clearSelectedCampaign } = useBoundStore(
    useShallow((state) => ({
      selectedCampaign: state.selectedCampaign,
      clearSelectedCampaign: state.clearSelectedCampaign,
    })),
  );

  const search = useBoundStore((state) => state.campaignManagerSearch);
  const selectedBrand = useBoundStore(
    (state) => state.campaignManagerSelectedBrand,
  );
  const selectedCategoryPath = useBoundStore(
    (state) => state.campaignManagerSelectedCategoryPath,
  );
  const selectedAdStates = useBoundStore(
    (state) => state.campaignManagerSelectedAdStates,
  );
  const paginationModel = useBoundStore(
    (state) => state.campaignManagerPaginationModel,
  );
  const setPaginationModel = useBoundStore(
    (state) => state.setCampaignManagerPaginationModel,
  );
  const setCampaignManagerRows = useBoundStore(
    (state) => state.setCampaignManagerRows,
  );
  const adsPaginationModel = useBoundStore(
    (state) => state.adsTablePaginationModel,
  );
  const setAdsPaginationModel = useBoundStore(
    (state) => state.setAdsTablePaginationModel,
  );

  const { data: campaignData, isLoading: isLoadingCampaignData } =
    useCampaignQuery();

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [
    search,
    selectedBrand,
    selectedCategoryPath,
    selectedAdStates,
    setPaginationModel,
  ]);

  // Filtrado de campañas
  const filteredCampaigns = useMemo(() => {
    const rawData = campaignData?.data ?? [];
    if (!Array.isArray(rawData)) return [];

    return rawData.filter((campaign) => {
      // Filtro por búsqueda (tipo inferido)
      if (Array.isArray(search) && search.length > 0) {
        const matchesAll = search.every(({ value, type }) => {
          const lower = value.toLowerCase();
          if (type === "MLA") {
            return campaign.ads?.some(
              (ad) => String(ad.itemId || "").toLowerCase() === lower,
            );
          }
          if (type === "SKU") {
            return (
              String(campaign.sku || "").toLowerCase() === lower ||
              campaign.ads?.some(
                (ad) => String(ad.sku || "").toLowerCase() === lower,
              )
            );
          }
          if (type === "Título") {
            return campaign.ads?.some((ad) =>
              String(ad.title || "")
                .toLowerCase()
                .includes(lower),
            );
          }
          return (
            String(campaign.campaignName || "")
              .toLowerCase()
              .includes(lower) ||
            String(campaign.sku || "")
              .toLowerCase()
              .includes(lower)
          );
        });
        if (!matchesAll) return false;
      }

      // Filtro por marca
      if (selectedBrand && selectedBrand.length > 0) {
        const brandTarget = Array.isArray(selectedBrand)
          ? selectedBrand[0]
          : selectedBrand;

        if (brandTarget) {
          const hasMatchingAd = campaign.ads.some((ad) => {
            if (brandTarget === "SIN ASIGNAR") {
              return ad.brand === null || ad.brand === undefined;
            } else {
              return ad.brand === brandTarget;
            }
          });

          if (!hasMatchingAd) return false;
        }
      }

      // Filtro por category path
      if (selectedCategoryPath && selectedCategoryPath.length > 0) {
        if (
          !campaign.ads.some((ad) =>
            selectedCategoryPath.includes(ad.categoryPath),
          )
        ) {
          return false;
        }
      }

      // Filtro por ad state
      if (
        selectedAdStates &&
        selectedAdStates.length > 0 &&
        selectedAdStates.length < 5
      ) {
        if (!selectedAdStates.includes(campaign.adState)) return false;
      }

      return true;
    });
  }, [
    campaignData,
    search,
    selectedBrand,
    selectedCategoryPath,
    selectedAdStates,
  ]);

  useEffect(() => {
    setCampaignManagerRows(filteredCampaigns);
  }, [filteredCampaigns, setCampaignManagerRows]);

  // Paginación de campañas
  const paginatedCampaigns = useMemo(() => {
    const { page, pageSize } = paginationModel;
    return filteredCampaigns.slice(page * pageSize, (page + 1) * pageSize);
  }, [filteredCampaigns, paginationModel]);

  // Filtrado de ads
  const filteredAds = useMemo(() => {
    if (!selectedCampaign) return [];

    const rawData = campaignData?.data ?? [];
    const campaignRow = rawData.find((row) => row.id === selectedCampaign.id);
    const ads = campaignRow?.ads ?? [];

    return ads.filter((ad) => {
      //En ads table no filtrar por campaña
      const isCampaignSearch = search.some((item) => item.type === "Campaña");
      if (Array.isArray(search) && search.length > 0 && !isCampaignSearch) {
        const matchesSearch = search.every(({ value, type }) => {
          const lower = value.toLowerCase();
          const adTitle = String(ad.title || "").toLowerCase();
          const adItemId = String(ad.itemId || "").toLowerCase();
          const adSku = String(ad.sku || "").toLowerCase();

          if (type === "MLA") return adItemId === lower;
          if (type === "SKU") return adSku === lower;
          if (type === "Título") return adTitle.includes(lower);

          // Default: busca en todos los campos
          return (
            adTitle.includes(lower) ||
            adItemId.includes(lower) ||
            adSku.includes(lower)
          );
        });
        if (!matchesSearch) return false;
      }

      // 2. FILTRO POR BRAND
      if (selectedBrand && selectedBrand.length > 0) {
        const brandTarget = Array.isArray(selectedBrand)
          ? selectedBrand[0]
          : selectedBrand;

        if (brandTarget === "SIN ASIGNAR") {
          if (ad.brand !== null && ad.brand !== undefined) return false;
        } else {
          if (ad.brand !== brandTarget) return false;
        }
      }

      // 3. FILTRO POR CATEGORY PATH
      if (selectedCategoryPath && selectedCategoryPath.length > 0) {
        if (!selectedCategoryPath.includes(ad.categoryPath)) {
          return false;
        }
      }

      return true;
    });
  }, [
    campaignData,
    selectedCampaign,
    search,
    selectedBrand,
    selectedCategoryPath,
  ]);

  useEffect(() => {
    setAdsPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [selectedCampaign, search, setAdsPaginationModel]);

  // Paginación de ads
  const paginatedAds = useMemo(() => {
    const { page, pageSize } = adsPaginationModel;
    return filteredAds.slice(page * pageSize, (page + 1) * pageSize);
  }, [filteredAds, adsPaginationModel]);

  const isAdsPage = page === "ads";

  useEffect(() => {
    // Si estamos en la página de ads y no hay campaña seleccionada, redirigimos a la página de campañas
    if (isAdsPage && !selectedCampaign) {
      navigate("/martech/campaign-manager");
    }
    // Si no estamos en la página de ads y hay campaña seleccionada, limpiamos la campaña seleccionada
    if (!isAdsPage && selectedCampaign) {
      clearSelectedCampaign();
    }
  }, [isAdsPage, selectedCampaign, navigate, clearSelectedCampaign]);

  if (isLoadingCampaignData || !campaignData)
    return <CampaignManagerSkeletons />;

  if (isAdsPage && !selectedCampaign) return null;

  return (
    <Stack spacing="24px">
      <Box
        sx={{
          backgroundColor: "white",
          height: "auto",
          width: "100%",
          borderRadius: "4px",
          background: "#FFF",
        }}
      >
        {isAdsPage && selectedCampaign ? (
          <AdsPage paginatedAds={paginatedAds} filteredAds={filteredAds} />
        ) : (
          <CampaignPage
            campaignData={campaignData}
            paginatedCampaigns={paginatedCampaigns}
            filteredCampaigns={filteredCampaigns}
          />
        )}
      </Box>
    </Stack>
  );
}
